import { Badge } from '@deepguide-ai/ui/badge'
import type { TestInclusion } from '@deepguide-ai/types'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'

/**
 * RFC-234 — the inclusion chip, on every Test at every program depth.
 *
 * `core` renders nothing: it is most of the list, and absence means owed.
 * `extended` marks depth beyond a first audit (the Enterprise-ready set). In a
 * Baseline workspace the deferral badge beside it explains why the row is out;
 * in an Enterprise-ready workspace the chip is purely informational —
 * visibility and a manual path, never an automatic downgrade. `scope` renders
 * nothing either: a deferred row already says so, and the Test page states
 * the answer it rests on (`TestTitleSection`).
 */
export function inclusionChipContent(
  inclusion?: TestInclusion | null,
): { label: string; explanation: string } | null {
  switch (inclusion) {
    case 'extended':
      return {
        label: 'Extended',
        explanation:
          'Depth beyond a first audit, typical of larger or more mature security programs. Part of the Enterprise-ready set; a Baseline program defers it until you turn it on.',
      }
    default:
      return null
  }
}

/** The list version: a native `title`, so a long page does not mount a Radix
 *  tooltip per row. */
export function TestInclusionChip({
  inclusion,
  className,
}: {
  inclusion?: TestInclusion | null
  className?: string
}) {
  const content = inclusionChipContent(inclusion)
  if (!content) return null
  return (
    <Badge
      status={inclusion === 'extended' ? 'purple' : 'default'}
      className={className}
      title={content.explanation}
    >
      {content.label}
    </Badge>
  )
}

/**
 * The detail-page version: same meaning, with a real tooltip, because this is
 * where someone stops to ask "why am I being asked for this?".
 */
export function TestInclusionBadge({
  inclusion,
  className,
}: {
  inclusion?: TestInclusion | null
  className?: string
}) {
  const content = inclusionChipContent(inclusion)
  if (!content) return null
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>
          <Badge
            status={inclusion === 'extended' ? 'purple' : 'default'}
            className='cursor-help'
          >
            {content.label}
          </Badge>
        </span>
      </TooltipTrigger>
      {/* Opens downward: the badge sits directly under the page title, and the
          default upward placement covered it. */}
      <TooltipContent side='bottom' align='start' className='max-w-xs'>
        <p className='text-xs leading-relaxed'>{content.explanation}</p>
      </TooltipContent>
    </Tooltip>
  )
}

const DEFERRAL_LABEL: Record<string, string> = {
  // RFC-234 M2 kinds: EXTENDED = depth beyond a first audit, SCOPE = waits on
  // a workspace fact. (M3 rewrites the SCOPE copy to name the fact.)
  EXTENDED: 'Deferred · Enterprise-ready',
  SCOPE: 'Deferred · confirm applicability',
  // RFC-225: names the audit type that DOES examine it, so the label reads as
  // "later" rather than "never" — the same reason DEPTH says Enterprise-ready.
  AUDIT_TYPE: 'Deferred · Type II',
  MANUAL: 'Deferred by you',
  // Auditor-only, synthesized per cycle by `auditorScopeView` — the tuple is
  // core only because of another enrolled framework (`coreFor`). Never stored,
  // so a customer surface cannot render it.
  OUT_OF_FRAMEWORK: 'Deferred · outside this framework',
}

export type TestDeferralKind = keyof typeof DEFERRAL_LABEL

/**
 * Distinguishes a deferred Test (a roadmap item, one click from active) from a
 * plain N/A (a verdict). Rendering them the same is what would make a lighter
 * default read as a claim that the deferred work never applies.
 */
export function TestDeferralBadge({
  deferralKind,
  deferralReason,
  className,
}: {
  deferralKind?: TestDeferralKind | null
  deferralReason?: string | null
  className?: string
}) {
  if (!deferralKind) return null
  return (
    <Badge
      status='amber'
      className={className}
      title={deferralReason ?? undefined}
    >
      {DEFERRAL_LABEL[deferralKind] ?? 'Deferred'}
    </Badge>
  )
}
